/**
 * ComposerPage — `/sales/invoices/new` orchestrator. Owns the form state
 * machine, the autosave debounce, the live preview, and the
 * "Issue and send" split-button.
 *
 * State shape (single useState — easier to reason about than 8
 * useStates and a useReducer would be premature for a 5-field form):
 *
 *   - `customer: CustomerSummary | null`
 *   - `lines: ComposerLineRow[]` (always at least 1 row)
 *   - `placeOfSupplyCode: string` (default = customer's GSTIN
 *      prefix, then customer state, then seller state)
 *   - `paymentTermDays: number` (drives the auto due date)
 *   - `issuedOn / dueOn / poReference / customerNote / internalNote`
 *   - `sendVia: ComposerSendChannel`
 *
 * Autosave runs after a 800ms idle, but ONLY once a customer is
 * picked + at least one line has a description (the minimum the
 * server accepts). The first save calls `createDraft`, subsequent
 * saves call `updateDraft` with the persisted draftId.
 *
 * "Issue and send" calls `invoices.issue` with `sendVia` so the
 * router does both atomically; on success it navigates to
 * `/sales/invoices/<id>` (the detail page).
 *
 * @see docs/spec/11-new-invoice-screen-spec.md — section-by-section
 *      contract.
 * @see prototype/sales/invoice-new.html — V1.1 visual baseline (S12).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  Copy as CopyIcon,
  FileText,
  Trash2,
} from "lucide-react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  ConfirmDialog,
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Input,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  StatusPill,
  toast,
} from "@flint/ui";
import { trpc } from "../../lib/trpc.ts";
import { CustomerPicker, type CustomerSummary } from "./customer-picker.tsx";
import { LineItemsTable, type ComposerLineRow } from "./line-items-table.tsx";
import { DatesAndNotesSection } from "./dates-and-notes-section.tsx";
import { SendSettings, type ComposerSendChannel } from "./send-settings.tsx";
import { RiskCard, formatCreditDetail, type RiskState } from "./risk-card.tsx";
import { LivePreviewPanel } from "./live-preview-panel.tsx";
import { SectionHeading } from "./section-heading.tsx";
import { AiHint, AiTrailingButton } from "./ai-hint.tsx";
import { HsnLookupDialog } from "./components/hsn-lookup-dialog.tsx";
import {
  computeInvoiceTotals,
  computeLineTax,
  computeLineForeign,
  convertForeignRateToPaise,
} from "./lib/gst-calc-shim.ts";
import { STATES_FOR_PICKER, stateCodeFromGstin, stateCodeFromName } from "./lib/state-codes.ts";
import { formatRupees } from "./lib/format-money.ts";
import { setInvoiceListFlash } from "./lib/list-flash.ts";

interface ComposerState {
  draftId: string | null;
  customer: CustomerSummary | null;
  lines: ComposerLineRow[];
  placeOfSupplyCode: string;
  paymentTermDays: number;
  issuedOn: string;
  dueOn: string;
  poReference: string;
  customerNote: string;
  internalNote: string;
  declaration: string;
  /** Dispatch warehouse for stock deduction on issue. Empty = use company default. */
  warehouseId: string;
  sendVia: ComposerSendChannel;
  currencyCode: "INR" | "USD" | "AED";
  foreignAmount: string;
  fxRate: string;
  fxRateDate: string;
  /** Tax treatment (ADR-0019). Drives whether line tax rates are forced to 0. */
  taxTreatment: TaxTreatment;
}

/**
 * Tax-treatment options the operator can pick. Per ADR-0019, domestic
 * (currency='INR') invoices use the first five; foreign invoices use the
 * last three. The composer flips the visible options when currency changes.
 */
type TaxTreatment =
  | "taxable_cgst_sgst"
  | "taxable_igst"
  | "nil_rated"
  | "exempt"
  | "out_of_scope"
  | "export_lut"
  | "export_with_igst"
  | "sez_supply";

const TAX_TREATMENT_DOMESTIC_OPTIONS: { value: TaxTreatment; label: string }[] = [
  { value: "taxable_cgst_sgst", label: "Taxable · CGST + SGST (intra-state)" },
  { value: "taxable_igst", label: "Taxable · IGST (inter-state)" },
  { value: "nil_rated", label: "Nil-rated (HSN attracts 0%)" },
  { value: "exempt", label: "Exempt supply" },
  { value: "out_of_scope", label: "Out of scope (composition / non-GST)" },
];
const TAX_TREATMENT_FOREIGN_OPTIONS: { value: TaxTreatment; label: string }[] = [
  { value: "export_lut", label: "Export under LUT — zero-rated, no IGST charged" },
  { value: "export_with_igst", label: "Export with IGST + refund" },
  { value: "sez_supply", label: "SEZ supply" },
];

/** True if the treatment forces every line tax rate to 0. */
const TAX_TREATMENT_ZERO_RATED_SET = new Set<TaxTreatment>([
  "nil_rated",
  "exempt",
  "out_of_scope",
  "export_lut",
  "sez_supply",
]);

function defaultTaxTreatmentFor(currencyCode: string): TaxTreatment {
  return currencyCode === "INR" ? "taxable_cgst_sgst" : "export_lut";
}

const DEFAULT_TERM_DAYS = 15;
const PAYMENT_TERMS = [
  { days: 0, label: "Due on receipt" },
  { days: 7, label: "Net 7 days" },
  { days: 15, label: "Net 15 days" },
  { days: 30, label: "Net 30 days" },
  { days: 45, label: "Net 45 days" },
];
const CURRENCY_OPTIONS = [
  { code: "INR", label: "INR · Indian rupee" },
  { code: "USD", label: "USD · US dollar" },
  { code: "AED", label: "AED · UAE dirham" },
] as const;

/** UUID we generate so React's `key` is stable across renders. */
const newKey = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `tmp-${Math.random().toString(36).slice(2)}`;

function emptyLine(): ComposerLineRow {
  return {
    key: newKey(),
    itemId: null,
    description: "",
    hsn: "",
    qtyMilli: 1000,
    unit: "Nos",
    ratePaise: 0n,
    rateForeignMinor: null,
    discountBps: 0,
    taxRateBps: 1800, // 18% — most-common SME rate
  };
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Days between two ISO dates. Rounds; clamps to 0..365. */
function termDaysBetween(fromIso: string, toIso: string): number {
  const from = new Date(`${fromIso}T00:00:00Z`).getTime();
  const to = new Date(`${toIso}T00:00:00Z`).getTime();
  const days = Math.round((to - from) / (1000 * 60 * 60 * 24));
  return Math.min(Math.max(days, 0), 365);
}

export interface ComposerPageProps {
  /**
   * When set, the composer loads this draft (via `invoices.get`) and
   * hydrates its form state. Subsequent autosaves call `updateDraft`
   * against the same id. Used by the `/sales/invoices/$invoiceId/edit` route.
   */
  loadDraftId?: string | undefined;
  /**
   * When set, the composer opens with this customer preselected — used by
   * the customer detail page's "New invoice" / "New invoice for X" links
   * (CUSTOMERS-DETAIL-AC17). Ignored when `loadDraftId` is present because
   * the draft carries its own customer. Fetched via `customers.get` on
   * mount; a bad id falls through to the empty picker rather than blocking.
   */
  preselectCustomerId?: string | undefined;
}

export function ComposerPage({ loadDraftId, preselectCustomerId }: ComposerPageProps = {}) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const company = trpc.companies.get.useQuery({});
  const warehouseList = trpc.warehouses.list.useQuery({ includeInactive: false });
  const warehouses = warehouseList.data?.warehouses ?? [];
  // Seeded FX reference rates (migration 0059) — used to suggest the booked
  // rate when the operator switches to a foreign currency, replacing the old
  // hard-coded placeholder.
  const fxRatesQuery = trpc.fxRates.list.useQuery();
  const fxRateRupeesByCurrency = useMemo(() => {
    const map: Record<string, string> = {};
    for (const r of fxRatesQuery.data ?? []) {
      // rate_paise → rupees (e.g. 9570 → "95.70").
      map[r.baseCurrency] = (Number(r.ratePaise) / 100).toString();
    }
    return map;
  }, [fxRatesQuery.data]);

  // Load existing draft when the route supplies an id. The query is
  // disabled when no id is passed so the new-invoice path doesn't
  // pay the round-trip.
  const loadedDraft = trpc.invoices.get.useQuery(
    { invoiceId: loadDraftId ?? "" },
    { enabled: !!loadDraftId },
  );

  // ── Initial state ────────────────────────────────────────────────
  const today = todayIso();
  const [state, setState] = useState<ComposerState>(() => ({
    draftId: null,
    customer: null,
    lines: [emptyLine()],
    placeOfSupplyCode: "",
    paymentTermDays: DEFAULT_TERM_DAYS,
    issuedOn: today,
    dueOn: addDaysIso(today, DEFAULT_TERM_DAYS),
    poReference: "",
    customerNote: "",
    internalNote: "",
    declaration: "",
    warehouseId: "",
    sendVia: "whatsapp",
    currencyCode: "INR",
    foreignAmount: "",
    fxRate: "",
    fxRateDate: today,
    taxTreatment: "taxable_cgst_sgst",
  }));

  // Hydrate from the loaded draft once it lands. Guarded by `draftId`
  // so a second render doesn't clobber operator edits in progress.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    if (!loadDraftId) return;
    if (!loadedDraft.data) return;
    const inv = loadedDraft.data.invoice;
    if (inv.status !== "draft") {
      // Not editable — kick the operator to the read-only detail page.
      void navigate({ to: "/sales/invoices/$invoiceId", params: { invoiceId: inv.id } });
      return;
    }
    hydratedRef.current = true;
    const customer = loadedDraft.data.customer;
    setState({
      draftId: inv.id,
      customer: {
        id: customer.id,
        name: customer.name,
        contactName: null,
        gstin: customer.gstin,
        phone: customer.phone,
        state: customer.state,
        city: null,
        paymentTerms: null,
        creditLimitPaise: null,
      },
      lines: loadedDraft.data.lines.map((l) => ({
        key: newKey(),
        itemId: l.itemId,
        description: l.description,
        hsn: l.hsn,
        unit: l.unit,
        qtyMilli: l.qtyMilli,
        ratePaise: BigInt(l.ratePaise),
        rateForeignMinor: l.rateForeignMinor !== null ? BigInt(l.rateForeignMinor) : null,
        discountBps: l.discountBps,
        taxRateBps: l.taxRateBps,
      })),
      placeOfSupplyCode: inv.placeOfSupply,
      paymentTermDays: termDaysBetween(inv.issuedOn, inv.dueOn),
      issuedOn: inv.issuedOn,
      dueOn: inv.dueOn,
      // PR-B — notes round-trip on edit-draft. Before this PR the invoice
      // schema had no columns for these so the contract dropped them on
      // save and the form re-initialised to '' on every reload — silent
      // data loss. The columns now exist (PR-B.1) and `invoiceRow` carries
      // them on the wire (PR-B.4); seed from the loaded row.
      poReference: inv.poReference ?? "",
      customerNote: inv.customerNote ?? "",
      internalNote: inv.internalNote ?? "",
      declaration: (inv as { declaration?: string | null }).declaration ?? "",
      warehouseId: (inv as { dispatchWarehouseId?: string | null }).dispatchWarehouseId ?? "",
      sendVia: "whatsapp",
      currencyCode: (inv.currencyCode as ComposerState["currencyCode"]) ?? "INR",
      foreignAmount: inv.foreignAmountMinor ? minorToMajor(BigInt(inv.foreignAmountMinor)) : "",
      fxRate: inv.fxRateToInrPaise ? minorToMajor(BigInt(inv.fxRateToInrPaise)) : "",
      fxRateDate: inv.fxRateDate ?? inv.issuedOn,
      taxTreatment:
        (inv.taxTreatment as TaxTreatment) ?? defaultTaxTreatmentFor(inv.currencyCode ?? "INR"),
    });
  }, [loadDraftId, loadedDraft.data, navigate]);

  // NEW-invoice Declaration prefill (COMPANY-AC23). Populates the field
  // with the company's `defaultInvoiceDeclaration` so the operator sees a
  // real editable value, not just a placeholder ghost. Fires once per
  // mount, only on the new-invoice path (edit-draft has its own
  // hydration above that seeds from the loaded row). Guarded so a
  // background refetch of `company.data` can't clobber operator edits
  // already in progress.
  const prefilledDeclFromCompanyRef = useRef(false);
  useEffect(() => {
    if (loadDraftId) return;
    if (prefilledDeclFromCompanyRef.current) return;
    const companyDefault = (company.data as { defaultInvoiceDeclaration?: string | null } | undefined)
      ?.defaultInvoiceDeclaration;
    if (!companyDefault) return;
    prefilledDeclFromCompanyRef.current = true;
    setState((s) => (s.declaration === "" ? { ...s, declaration: companyDefault } : s));
  }, [loadDraftId, company.data]);

  // ── Derived: company state code (for tax-mode calc) ─────────────
  const companyStateCode = useMemo(
    () =>
      stateCodeFromGstin(company.data?.gstin ?? null) ??
      stateCodeFromName(company.data?.state ?? null) ??
      "",
    [company.data?.gstin, company.data?.state],
  );

  // ── Derived: FX rate (foreign-to-INR) + foreign mode flag ────────
  const fxRateToInrPaise = useMemo(() => majorToMinor(state.fxRate), [state.fxRate]);
  const isForeign = state.currencyCode !== "INR";

  // ── Derived: per-line + invoice totals (live) ───────────────────
  //
  // ADR-0020. For foreign-currency invoices:
  //   1. Compute foreign-minor per-line values (gross/disc/taxable/tax/total)
  //      directly from the operator-typed `rateForeignMinor` — these are
  //      what the customer sees on the invoice.
  //   2. Derive each line's `ratePaise` from `rateForeignMinor *
  //      fxRateToInrPaise / 100` (banker's-rounded). This is what gets
  //      persisted to the INR ledger.
  //   3. Run the existing `computeLineTax` + `computeInvoiceTotals` on
  //      the derived INR rates — same rollup as today, no special cases.
  //
  // For INR invoices, `ratePaise` is operator-typed; foreign values stay null.
  const { computedLines, computedForeign, totals, foreignTotals } = useMemo(() => {
    // Derive each line's INR ratePaise from foreign rate when in foreign mode.
    const inrRates = state.lines.map((l) =>
      isForeign && l.rateForeignMinor !== null && fxRateToInrPaise > 0n
        ? convertForeignRateToPaise(l.rateForeignMinor, fxRateToInrPaise)
        : l.ratePaise,
    );

    const cls = state.lines.map((l, i) =>
      computeLineTax({
        qtyMilli: l.qtyMilli,
        ratePaise: inrRates[i]!,
        discountBps: l.discountBps,
        taxRateBps: l.taxRateBps,
      }),
    );
    const t = computeInvoiceTotals({
      lines: cls,
      companyStateCode,
      placeOfSupplyCode: state.placeOfSupplyCode || companyStateCode,
    });

    let cf: ReturnType<typeof computeLineForeign>[] | undefined;
    let ft:
      | {
          subtotalForeign: bigint;
          taxableForeign: bigint;
          taxForeign: bigint;
          totalForeign: bigint;
        }
      | undefined;
    if (isForeign) {
      cf = state.lines.map((l) =>
        computeLineForeign({
          qtyMilli: l.qtyMilli,
          rateForeignMinor: l.rateForeignMinor ?? 0n,
          discountBps: l.discountBps,
          taxRateBps: l.taxRateBps,
        }),
      );
      ft = {
        subtotalForeign: cf.reduce((a, c) => a + c.grossForeignMinor, 0n),
        taxableForeign: cf.reduce((a, c) => a + c.taxableForeignMinor, 0n),
        taxForeign: cf.reduce((a, c) => a + c.taxForeignMinor, 0n),
        totalForeign: cf.reduce((a, c) => a + c.lineTotalForeignMinor, 0n),
      };
    }

    return { computedLines: cls, computedForeign: cf, totals: t, foreignTotals: ft };
  }, [state.lines, state.placeOfSupplyCode, companyStateCode, isForeign, fxRateToInrPaise]);

  // For non-INR invoices, the operator-facing "foreign total" is derived
  // from line items, not typed at the top. We still send it on the wire
  // so the server's `build-fx-snapshot` invariant is satisfied.
  const foreignAmountMinor = foreignTotals?.totalForeign ?? 0n;

  // ── Currency-switch confirmation (ADR-0020) ──────────────────────
  // Switching invoice currency invalidates per-line rates (USD → INR
  // would mean the operator's "$300" line is suddenly "₹300", and vice
  // versa). Surface a confirm prompt before clearing; no-op when the
  // grid is empty.
  const [currencySwitchTarget, setCurrencySwitchTarget] = useState<
    ComposerState["currencyCode"] | null
  >(null);

  const applyCurrencyChange = useCallback(
    (next: ComposerState["currencyCode"], clearRates: boolean) => {
      setState((p) => {
        if (p.currencyCode === next) return p;
        const becomingForeign = next !== "INR";
        const wasForeign = p.currencyCode !== "INR";
        const nextLines = clearRates
          ? p.lines.map((l) => ({
              ...l,
              ratePaise: 0n,
              rateForeignMinor: becomingForeign ? 0n : null,
            }))
          : p.lines.map((l) => ({
              ...l,
              // Toggle the foreign field on/off so the per-line invariant
              // holds (non-INR ⇒ rateForeignMinor present; INR ⇒ absent).
              rateForeignMinor: becomingForeign ? (l.rateForeignMinor ?? 0n) : null,
            }));
        return {
          ...p,
          currencyCode: next,
          lines: nextLines,
          // Export invoices supply a recipient outside India — place of supply
          // is "Outside India" (96), never an Indian state. Setting it here
          // also stops the empty-PoS Save no-op on foreign drafts. Switching
          // back to INR re-derives PoS from the company state.
          placeOfSupplyCode: becomingForeign
            ? "96"
            : p.placeOfSupplyCode === "96"
              ? companyStateCode || ""
              : p.placeOfSupplyCode,
          taxTreatment:
            next === "INR"
              ? TAX_TREATMENT_DOMESTIC_OPTIONS.some((o) => o.value === p.taxTreatment)
                ? p.taxTreatment
                : "taxable_cgst_sgst"
              : TAX_TREATMENT_FOREIGN_OPTIONS.some((o) => o.value === p.taxTreatment)
                ? p.taxTreatment
                : "export_lut",
          ...(next === "INR"
            ? { foreignAmount: "", fxRate: "", fxRateDate: p.issuedOn }
            : {
                fxRateDate: p.fxRateDate || p.issuedOn,
                // Pre-fill the booked rate from the seeded FX table when first
                // switching into a foreign currency; leave a typed rate alone.
                ...(wasForeign ? {} : { fxRate: fxRateRupeesByCurrency[next] ?? "" }),
              }),
        };
      });
    },
    [companyStateCode, fxRateRupeesByCurrency],
  );

  const requestCurrencyChange = useCallback(
    (next: ComposerState["currencyCode"]) => {
      if (state.currencyCode === next) return;
      const hasNonZeroRate = state.lines.some((l) =>
        state.currencyCode === "INR" ? l.ratePaise !== 0n : (l.rateForeignMinor ?? 0n) !== 0n,
      );
      if (!hasNonZeroRate) {
        applyCurrencyChange(next, false);
        return;
      }
      setCurrencySwitchTarget(next);
    },
    [state.currencyCode, state.lines, applyCurrencyChange],
  );

  // ── Customer selection side effects ──────────────────────────────
  //
  // PoS resolution honours `companies.default_place_of_supply` (Settings →
  // GSTIN & branches). Three modes:
  //   - `from_customer`        — auto from customer GSTIN/state (recommended).
  //   - `always_sender_state`  — pin to company state regardless of customer.
  //   - `ask_per_invoice`      — clear PoS so the operator picks explicitly.
  const posMode = company.data?.defaultPlaceOfSupply ?? "from_customer";
  const onCustomerSelect = useCallback(
    (c: CustomerSummary) => {
      setState((prev) => {
        let nextPos: string;
        if (prev.currencyCode !== "INR") {
          // Foreign/export invoice — recipient is outside India (96), no Indian
          // state applies regardless of the PoS mode.
          nextPos = "96";
        } else if (posMode === "always_sender_state") {
          nextPos = companyStateCode || prev.placeOfSupplyCode;
        } else if (posMode === "ask_per_invoice") {
          nextPos = "";
        } else {
          // from_customer — current behaviour
          nextPos = stateCodeFromGstin(c.gstin) ?? stateCodeFromName(c.state) ?? prev.placeOfSupplyCode;
        }
        // Default the payment-term picker to the customer's saved
        // terms when they're a parseable "Net N" or "N" string. Else
        // keep the current value.
        const termDays = parseTermDays(c.paymentTerms) ?? prev.paymentTermDays;
        const dueOn = addDaysIso(prev.issuedOn, termDays);
        return {
          ...prev,
          customer: c,
          placeOfSupplyCode: nextPos,
          paymentTermDays: termDays,
          dueOn,
        };
      });
    },
    [posMode, companyStateCode],
  );

  // NEW-invoice customer preselect (CUSTOMERS-DETAIL-AC17). When arriving
  // from the customer detail page's "New invoice" / "New invoice for X"
  // link (which carries `?customerId=<id>`), fetch that customer and drop
  // it into the picker so the operator lands on the item step instead of
  // re-picking the customer they just came from. Guarded so a background
  // refetch of `preselectQuery.data` can't clobber a later manual pick.
  const preselectQuery = trpc.customers.get.useQuery(
    { id: preselectCustomerId ?? "" },
    { enabled: !!preselectCustomerId && !loadDraftId },
  );
  const preselectHydratedRef = useRef(false);
  useEffect(() => {
    if (loadDraftId) return;
    if (preselectHydratedRef.current) return;
    if (!preselectQuery.data) return;
    preselectHydratedRef.current = true;
    const c = preselectQuery.data;
    // Adapt `customerRow` → `CustomerSummary` so onCustomerSelect can
    // handle PoS + terms + credit exactly as if the picker had been used.
    onCustomerSelect({
      id: c.id,
      name: c.name,
      contactName: c.contactName,
      gstin: c.gstin,
      phone: c.phone,
      state: c.state,
      city: c.city,
      paymentTerms: c.paymentTerms,
      creditLimitPaise: c.creditLimitPaise,
    });
  }, [loadDraftId, preselectQuery.data, onCustomerSelect]);

  // ── Mutations ───────────────────────────────────────────────────
  // Track the last successful save so the head shows "Saved 4s ago" instead
  // of just "Saved" — the relative label refreshes via `useRelativeSavedLabel`.
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);

  const createDraft = trpc.invoices.createDraft.useMutation({
    onSuccess: (data) => {
      setState((prev) => ({ ...prev, draftId: data.invoice.id }));
      setLastSavedAt(Date.now());
      void utils.invoices.list.invalidate();
    },
  });
  const updateDraft = trpc.invoices.updateDraft.useMutation({
    onSuccess: () => {
      setLastSavedAt(Date.now());
      void utils.invoices.list.invalidate();
    },
  });
  const issue = trpc.invoices.issue.useMutation({
    onSuccess: (data) => {
      void utils.invoices.list.invalidate();
      if (data.outcome === "awaiting_approval" || data.invoice === null) {
        // A configured discount approval chain (ADR-0031) gated the issue —
        // the draft is untouched. Surface it and drop to the draft detail.
        toast.success("Sent for approval", {
          description: "This invoice's discount needs sign-off before it's issued.",
        });
        if (data.draftId) {
          void navigate({ to: "/sales/invoices/$invoiceId", params: { invoiceId: data.draftId } });
        }
        return;
      }
      void navigate({ to: "/sales/invoices/$invoiceId", params: { invoiceId: data.invoice.id } });
    },
  });
  const deleteDraft = trpc.invoices.deleteDraft.useMutation({
    onSuccess: () => {
      void utils.invoices.list.invalidate();
      // Delete naturally takes the operator back to the list (there's
      // nothing to keep editing); flash the confirmation there.
      setInvoiceListFlash({ kind: "draft-deleted", message: "Draft deleted." });
      void navigate({ to: "/sales/invoices" });
    },
  });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [hsnLookupOpen, setHsnLookupOpen] = useState(false);

  /**
   * Explicit Save-draft action — distinct from autosave. Persists the
   * current state and pops a Sonner toast confirming the save. The
   * operator stays on the composer so they can keep refining or save
   * again, mirroring the "save and continue" pattern most ERPs use.
   */
  const handleSaveDraft = useCallback(async () => {
    const payload = toPersistInput(state);
    if (!payload) return;
    try {
      if (state.draftId) {
        await updateDraft.mutateAsync({ ...payload, draftId: state.draftId });
      } else {
        await createDraft.mutateAsync(payload);
      }
      toast.success("Draft saved", {
        description: "Keep editing or use the back arrow to return to the invoice list.",
      });
    } catch (err) {
      toast.error("Couldn't save draft", {
        description: err instanceof Error ? err.message : "Try again in a moment.",
      });
    }
  }, [state, createDraft, updateDraft]);

  // ── Autosave (800ms idle, after customer + first line described) ─
  // Mirror of the `requireDueOnAfterIssuedOn` Zod refinement that the
  // server applies — surface the error inline before round-trip so the
  // operator can fix it without seeing a TRPC validation toast.
  const dueOnError =
    state.dueOn && state.issuedOn && state.dueOn < state.issuedOn
      ? "Due date must be on or after the issue date."
      : null;
  // ADR-0020 — foreign invoices need a positive FX rate AND at least one
  // line with a positive foreign rate (else the foreign total is zero).
  // Line rate validity is checked via the derived `foreignAmountMinor`
  // since that's the sum of `lineTotalForeignMinor`.
  const fxError =
    isForeign && fxRateToInrPaise <= 0n
      ? "Foreign invoices need an FX rate."
      : isForeign && foreignAmountMinor <= 0n
        ? "Add at least one line with a positive rate in the invoice currency."
        : null;
  // A described line with no positive quantity is invalid — the server's
  // `qtyMilli: z.number().int().positive()` rejects it. Block issue client-side and
  // tell the operator, rather than letting them click into a 400 (or, before the
  // preview fallback was removed, issue a bogus ₹0.12 line). QA finding F-AC5.
  const qtyError = state.lines.some(
    (l) => l.description.trim().length > 0 && l.qtyMilli <= 0,
  )
    ? "Every line needs a quantity greater than 0."
    : null;
  const ready =
    state.customer !== null &&
    state.lines.some((l) => l.description.trim().length > 0) &&
    qtyError === null &&
    dueOnError === null &&
    fxError === null;
  const lastSavedSig = useRef<string>("");
  useEffect(() => {
    if (!ready) return;
    const signature = serialize(state);
    if (signature === lastSavedSig.current) return;
    if (createDraft.isPending || updateDraft.isPending || issue.isPending) return;
    const t = setTimeout(() => {
      const payload = toPersistInput(state);
      if (!payload) return;
      lastSavedSig.current = signature;
      if (state.draftId) {
        updateDraft.mutate({ ...payload, draftId: state.draftId });
      } else {
        createDraft.mutate(payload);
      }
    }, 800);
    return () => clearTimeout(t);
  }, [state, ready, createDraft, updateDraft, issue.isPending]);

  // Warn before discarding unsaved composer content on refresh / tab-close /
  // hard back-nav. Dirty = the user entered something not yet persisted to a
  // draft; once autosave lands (signature matches lastSavedSig) the work is
  // safe and we stay silent. Client-side route changes don't trigger
  // `beforeunload`, so issuing → navigate is unaffected. (QA finding M01.)
  const hasComposerContent =
    state.customer !== null ||
    state.lines.some((l) => l.description.trim().length > 0) ||
    state.internalNote.trim().length > 0 ||
    state.customerNote.trim().length > 0 ||
    state.poReference.trim().length > 0;
  const composerDirty = hasComposerContent && serialize(state) !== lastSavedSig.current;
  useEffect(() => {
    if (!composerDirty) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [composerDirty]);

  // ── Risk states ──────────────────────────────────────────────────
  const gstinState: RiskState = state.customer?.gstin
    ? state.customer.state && stateCodeFromName(state.customer.state) !== state.placeOfSupplyCode
      ? "warn"
      : "ok"
    : "warn";
  const gstinDetail = state.customer?.gstin
    ? `GSTIN ${state.customer.gstin} · POS ${state.placeOfSupplyCode || "—"}`
    : "No GSTIN on file — invoice will be unregistered.";

  const maxDiscountBps = state.lines.reduce((m, l) => Math.max(m, l.discountBps), 0);
  const discountState: RiskState = maxDiscountBps > 1500 ? "warn" : "ok";
  const discountDetail =
    maxDiscountBps > 0
      ? `Highest line discount ${(maxDiscountBps / 100).toFixed(2)}% · 15% threshold`
      : "No line discounts applied";

  // Credit usage — limit comes from the customer master (PR-A.8 wired the
  // `creditLimitPaise` column through to listRow). Outstanding-vs-limit math
  // stays "pending" until the receipts vertical (V1.x) lands; until then we
  // can render the limit alone so the operator at least knows what it is.
  const creditLimitPaise = state.customer?.creditLimitPaise
    ? BigInt(state.customer.creditLimitPaise)
    : null;
  const creditState: RiskState = "pending";
  const creditDetail = formatCreditDetail(creditLimitPaise, null);

  // ── Issue handler ────────────────────────────────────────────────
  const handleIssue = useCallback(
    (channel: ComposerSendChannel) => {
      if (!state.customer) return;
      if (!ready) return;
      // Always go through `issue` with the (possibly-just-created)
      // draftId. If we don't have a draftId yet, fall back to inline
      // create-and-issue (the discriminated union the contract supports).
      const sendVia = channel;
      if (state.draftId) {
        issue.mutate({
          draftId: state.draftId,
          issuedOn: state.issuedOn,
          dueOn: state.dueOn,
          placeOfSupply: state.placeOfSupplyCode,
          sendVia,
        });
      } else {
        const payload = toPersistInput(state);
        if (!payload) return;
        issue.mutate({ ...payload, sendVia });
      }
    },
    [issue, ready, state],
  );

  // ── Hooks ABOVE early returns (Rules of Hooks) ──────────────────
  const isSaving = createDraft.isPending || updateDraft.isPending;
  const relativeSavedLabel = useRelativeSavedLabel(lastSavedAt);

  // ── Render ───────────────────────────────────────────────────────
  if (company.isLoading) {
    return <p className="text-sm text-muted">Loading composer…</p>;
  }
  if (company.error || !company.data) {
    return (
      <Alert
        intent="bad"
        title="Couldn't load company profile"
        description={
          company.error?.message ?? "Settings → Company profile is required to issue invoices."
        }
      />
    );
  }
  if (!companyStateCode) {
    return (
      <Alert
        intent="warn"
        title="Configure your company GSTIN or state"
        description="Flint needs your seller state to compute the GST split. Visit Settings → Company profile."
      />
    );
  }

  const saveStatus = isSaving
    ? "Saving…"
    : state.draftId
      ? `Saved ${relativeSavedLabel}`
      : "Unsaved";
  const saveStatusTone = isSaving ? "saving" : state.draftId ? "saved" : "idle";

  return (
    <div className="flex min-h-full flex-col">
      {/* Composer head — mirrors the prototype's "New invoice · Draft ·
       *  pending number" pill + Saved Xs ago + Duplicate previous. */}
      <header className="sticky top-0 z-20 flex min-h-14 shrink-0 flex-col items-stretch gap-3 border-b border-line bg-paper px-6 py-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="flex min-w-0 flex-1 items-center gap-3.5">
          <Link
            to="/sales/invoices"
            className="grid h-9 w-9 shrink-0 place-items-center rounded-sm border border-line bg-paper text-ink-2 transition-colors hover:bg-bg"
            aria-label="Back to invoices"
          >
            <ArrowLeft size={16} strokeWidth={2.2} />
          </Link>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 leading-none">
              <h1 className="text-base font-bold leading-tight text-ink">
                {loadDraftId ? "Edit invoice" : "New invoice"}
              </h1>
              <span className="inline-flex items-center rounded-full bg-raised px-2 py-0.5 font-mono text-xs font-medium text-muted">
                Draft · pending number
              </span>
            </div>
            <p className="mt-0.5 text-xs text-muted">
              Create a draft, issue now, or issue and send to the customer.
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className={saveStatusClassName(saveStatusTone)} aria-live="polite">
            <span aria-hidden className={saveStatusDotClassName(saveStatusTone)} />
            {saveStatus}
          </span>
          <DuplicatePreviousButton
            disabled={issue.isPending || isSaving}
            currentDraftId={state.draftId}
            companyStateCode={companyStateCode}
            onCloned={(invoiceId) => {
              void navigate({
                to: "/sales/invoices/$invoiceId/edit",
                params: { invoiceId },
              });
            }}
          />
        </div>
      </header>

      {/* 2-column layout: form (left) + preview (right) */}
      <div className="mx-auto grid w-full max-w-screen-2xl grid-cols-1 gap-5 px-6 py-5 xl:grid-cols-5">
        {/* Form column */}
        <div className="flex flex-col gap-4 min-w-0 xl:col-span-3">
          {/* Customer + billing context */}
          <Card>
            <SectionHeading
              step={1}
              title="Customer & billing context"
              description="Who is being invoiced. This decides the tax mode."
              trailing={
                <AiTrailingButton
                  label="Suggest customer"
                  title="AI customer suggestions land in V1.x."
                />
              }
            />
            <CardBody className="flex flex-col gap-3.5">
              <CustomerPicker
                selected={state.customer}
                onSelect={onCustomerSelect}
                disabled={issue.isPending}
              />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="pos-state">
                    Place of supply
                    <span aria-hidden className="ml-0.5 text-rose-600">
                      *
                    </span>
                  </Label>
                  <Select
                    value={state.placeOfSupplyCode}
                    onValueChange={(v) => setState((p) => ({ ...p, placeOfSupplyCode: v }))}
                  >
                    <SelectTrigger id="pos-state">
                      <SelectValue placeholder="Pick a state…" />
                    </SelectTrigger>
                    <SelectContent>
                      {STATES_FOR_PICKER.map((s) => (
                        <SelectItem key={s.code} value={s.code}>
                          {s.name} ({s.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted">Auto-derived from customer GSTIN.</p>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="payment-terms">Payment terms</Label>
                  <Select
                    value={String(state.paymentTermDays)}
                    onValueChange={(v) => {
                      const days = Number(v);
                      setState((p) => ({
                        ...p,
                        paymentTermDays: days,
                        dueOn: addDaysIso(p.issuedOn, days),
                      }));
                    }}
                  >
                    <SelectTrigger id="payment-terms">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_TERMS.map((t) => (
                        <SelectItem key={t.days} value={String(t.days)}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted">Default from customer record.</p>
                </div>
                {/* Credit status — spec §5.1.7. Tax mode lives in the
                 *  right-side live summary (spec §7 / §12.7), not here. */}
                <div className="flex flex-col gap-1.5">
                  <Label>Credit status</Label>
                  <div className="flex h-9 items-center gap-2">
                    {state.customer ? (
                      creditLimitPaise ? (
                        <>
                          <StatusPill tone="sky" size="sm">
                            Limit on file
                          </StatusPill>
                          <span className="text-sm text-muted font-mono">
                            ₹{formatRupees(creditLimitPaise).replace("₹", "")}
                          </span>
                        </>
                      ) : (
                        <StatusPill tone="muted" size="sm">
                          No limit set
                        </StatusPill>
                      )
                    ) : (
                      <span className="text-sm text-muted">Pick a customer first</span>
                    )}
                  </div>
                  <p className="text-sm text-muted">
                    {creditLimitPaise
                      ? "Usage math arrives with the receipts vertical (V1.x)."
                      : "Limit comes from the customer record."}
                  </p>
                </div>
              </div>

              {/* Deferred AI hint — Flint suggestions about terms /
               *  channel land in V1.x. Slot is reserved so the layout
               *  matches the prototype today. */}
              <AiHint
                body={
                  <>
                    <strong className="text-ink-2">Flint:</strong> suggestions about payment terms
                    and default channel appear here once the AI vertical ships (V1.x).
                  </>
                }
              />
            </CardBody>
          </Card>

          {/* Currency & tax treatment — ADR-0020: moved ABOVE line items.
              The chosen currency governs how rates are entered on each line. */}
          <Card>
            <SectionHeading
              step={2}
              title="Currency & tax treatment"
              description={
                isForeign
                  ? "Lines are entered in invoice currency. INR equivalent is derived from the booked FX rate."
                  : "Invoice is booked and displayed in INR."
              }
            />
            <CardBody className="flex flex-col gap-3.5">
              <div
                className={`grid grid-cols-1 gap-3.5 ${
                  isForeign ? "sm:grid-cols-3" : "sm:grid-cols-1"
                }`}
              >
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="invoice-currency">Currency</Label>
                  <Select
                    value={state.currencyCode}
                    onValueChange={(v) => requestCurrencyChange(v as ComposerState["currencyCode"])}
                  >
                    <SelectTrigger id="invoice-currency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCY_OPTIONS.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {isForeign ? (
                  <>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="fx-rate">FX rate to INR</Label>
                      <Input
                        id="fx-rate"
                        type="text"
                        inputMode="decimal"
                        value={state.fxRate}
                        disabled={issue.isPending}
                        onChange={(e) =>
                          setState((p) => ({ ...p, fxRate: decimalInput(e.target.value) }))
                        }
                        placeholder={fxRateRupeesByCurrency[state.currencyCode] ?? "e.g. 95.70"}
                        className="font-mono"
                      />
                      <span className="text-xs text-muted">
                        Paise per 1 {state.currencyCode}. Use the CBEC notified rate or RBI
                        reference rate on the invoice date.
                      </span>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="fx-date">Rate date</Label>
                      <Input
                        id="fx-date"
                        type="date"
                        value={state.fxRateDate}
                        disabled={issue.isPending}
                        onChange={(e) => setState((p) => ({ ...p, fxRateDate: e.target.value }))}
                        className="font-mono"
                      />
                    </div>
                  </>
                ) : null}
              </div>

              {/* ADR-0019 — Tax treatment selector with explicit zero-rated options */}
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="tax-treatment">Tax treatment</Label>
                <Select
                  value={state.taxTreatment}
                  onValueChange={(v) =>
                    setState((p) => {
                      const nextTreatment = v as TaxTreatment;
                      const isZeroRated = TAX_TREATMENT_ZERO_RATED_SET.has(nextTreatment);
                      const nextLines = isZeroRated
                        ? p.lines.map((l) => (l.taxRateBps === 0 ? l : { ...l, taxRateBps: 0 }))
                        : p.lines;
                      return {
                        ...p,
                        taxTreatment: nextTreatment,
                        lines: nextLines,
                      };
                    })
                  }
                >
                  <SelectTrigger id="tax-treatment">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(state.currencyCode === "INR"
                      ? TAX_TREATMENT_DOMESTIC_OPTIONS
                      : TAX_TREATMENT_FOREIGN_OPTIONS
                    ).map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-xs text-muted">
                  {TAX_TREATMENT_ZERO_RATED_SET.has(state.taxTreatment)
                    ? "Zero-rated — every line item must have tax rate 0%."
                    : state.currencyCode === "INR"
                      ? "Domestic taxable — line items carry CGST+SGST or IGST per place-of-supply."
                      : "Foreign export with IGST + refund — charge IGST at standard rates, claim refund later."}
                </span>
              </div>
            </CardBody>
          </Card>

          {/* Line items */}
          <Card>
            <SectionHeading
              step={3}
              title="Line items"
              description={
                isForeign
                  ? `Rates entered in ${state.currencyCode}. INR equivalent computed via the booked FX rate.`
                  : "What's being sold. HSN, qty, rate, GST."
              }
              trailing={
                <AiTrailingButton
                  label="Lookup HSN"
                  title="Open the HSN quick-pick"
                  onClick={() => setHsnLookupOpen(true)}
                />
              }
            />
            <CardBody className="flex flex-col gap-3.5">
              <LineItemsTable
                rows={state.lines}
                computed={computedLines}
                {...(computedForeign ? { computedForeign } : {})}
                currencyCode={state.currencyCode}
                disabled={issue.isPending}
                onChange={(key, patch) =>
                  setState((p) => ({
                    ...p,
                    lines: p.lines.map((l) => (l.key === key ? { ...l, ...patch } : l)),
                  }))
                }
                onAdd={() => setState((p) => ({ ...p, lines: [...p.lines, emptyLine()] }))}
                onRemove={(key) =>
                  setState((p) => ({
                    ...p,
                    lines: p.lines.length === 1 ? p.lines : p.lines.filter((l) => l.key !== key),
                  }))
                }
                summary={
                  <>
                    {state.lines.length} item{state.lines.length === 1 ? "" : "s"} ·{" "}
                    {isForeign && foreignTotals ? (
                      <>
                        <span className="font-mono">
                          {formatForeign(state.currencyCode, foreignTotals.taxableForeign)}
                        </span>{" "}
                        taxable
                      </>
                    ) : (
                      <>
                        <span className="font-mono">
                          ₹{formatRupees(totals.taxableValuePaise).replace("₹", "")}
                        </span>{" "}
                        taxable ·{" "}
                        {totals.taxMode === "intra"
                          ? "CGST + SGST"
                          : `IGST ${state.lines[0]?.taxRateBps ? state.lines[0].taxRateBps / 100 : 18}%`}
                      </>
                    )}
                  </>
                }
              />
            </CardBody>
          </Card>

          {/* FX preview & INR totals — ADR-0020 derived strip below lines. */}
          {isForeign ? (
            <Card>
              <SectionHeading
                step={4}
                title="FX preview & INR posting"
                description="Read-only. The customer sees the foreign side; the ledger books the INR side."
              />
              <CardBody className="flex flex-col gap-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 rounded-sm border border-line bg-raised px-3 py-3">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-muted">
                      Foreign total ({state.currencyCode})
                    </div>
                    <div className="font-mono text-base font-semibold text-ink">
                      {foreignTotals
                        ? formatForeign(state.currencyCode, foreignTotals.totalForeign)
                        : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-muted">
                      Booked FX rate
                    </div>
                    <div className="font-mono text-base font-semibold text-ink">
                      {fxRateToInrPaise > 0n ? `₹${state.fxRate}/${state.currencyCode}` : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-muted">
                      INR book value
                    </div>
                    <div className="font-mono text-base font-semibold text-ink">
                      {formatRupees(totals.grandTotalPaise)}
                    </div>
                  </div>
                </div>
                <p className="text-xs text-muted leading-relaxed">
                  The customer sees{" "}
                  <strong className="text-ink-2 font-mono">
                    {foreignTotals
                      ? formatForeign(state.currencyCode, foreignTotals.totalForeign)
                      : "—"}
                  </strong>
                  . The INR equivalent posts to the ledger at the booked rate; FX gain or loss is
                  realised on the receipt date when the customer actually pays.
                </p>
                {fxError ? <p className="text-xs font-medium text-rose-700">{fxError}</p> : null}
              </CardBody>
            </Card>
          ) : null}

          {/* Dates + notes */}
          <DatesAndNotesSection
            issuedOn={state.issuedOn}
            dueOn={state.dueOn}
            poReference={state.poReference}
            customerNote={state.customerNote}
            internalNote={state.internalNote}
            declaration={state.declaration}
            declarationPlaceholder={
              // Company default is now prefilled into `state.declaration`
              // on new-invoice mount (COMPANY-AC23), so the placeholder no
              // longer carries the "here's your default" duty. Keep the
              // foreign-currency LUT hint for the export path when no
              // company default is set.
              state.currencyCode !== "INR" && !state.declaration
                ? "Defaults to the Section-16 LUT declaration block."
                : ""
            }
            onChange={(patch) => setState((p) => ({ ...p, ...patch }))}
            dueHint={
              state.paymentTermDays > 0
                ? `Auto from net ${state.paymentTermDays} · ${state.dueOn}`
                : undefined
            }
            dueError={dueOnError}
            disabled={issue.isPending}
          />

          {/* Dispatch warehouse — shown when warehouses are configured */}
          {warehouses.length > 0 && (
            <div className="rounded-xl border border-line bg-paper px-5 py-4 space-y-2">
              <div>
                <p className="text-sm font-semibold text-ink">Dispatch warehouse</p>
                <p className="text-xs text-muted mt-0.5">
                  Stock is deducted here on issue for tracked goods items.
                </p>
              </div>
              <Select
                value={state.warehouseId || "__default__"}
                onValueChange={(v) => setState((p) => ({ ...p, warehouseId: v === "__default__" ? "" : v }))}
                disabled={issue.isPending}
              >
                <SelectTrigger className="!h-9 !text-sm w-full" aria-label="Dispatch warehouse">
                  <SelectValue placeholder="Company default" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__default__">Company default</SelectItem>
                  {warehouses.map((wh) => (
                    <SelectItem key={wh.id} value={wh.id}>
                      {wh.name}{wh.isDefault ? " (default)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Send settings */}
          <SendSettings
            value={state.sendVia}
            onChange={(v) => setState((p) => ({ ...p, sendVia: v }))}
            customerHasPhone={!!state.customer?.phone}
            customerHasEmail={
              false /* customers list row doesn't expose email; kept disabled in V1.0.x */
            }
            disabled={issue.isPending}
          />

          {/* Risk + approval */}
          <RiskCard
            gstinState={gstinState}
            gstinDetail={gstinDetail}
            discountState={discountState}
            discountDetail={discountDetail}
            creditState={creditState}
            creditDetail={creditDetail}
          />
        </div>

        {/* Preview column (sticky on xl screens) */}
        <aside className="xl:sticky xl:top-4 xl:col-span-2 xl:self-start flex flex-col gap-3">
          <LivePreviewPanel
            seller={{
              name: company.data.legalName,
              addressLines: [
                company.data.address ?? "",
                [company.data.city, company.data.state, company.data.pincode]
                  .filter(Boolean)
                  .join(", "),
              ].filter((s) => s.trim().length > 0),
              gstin: company.data.gstin,
              state: company.data.state,
              // PR-H — printed bank details for the live preview's "Pay to" footer.
              bankName: company.data.bankName,
              bankAccountNumber: company.data.bankAccountNumber,
              bankIfsc: company.data.bankIfsc,
              bankUpiId: company.data.bankUpiId,
            }}
            customer={
              state.customer
                ? {
                    name: state.customer.name,
                    gstin: state.customer.gstin,
                    state: state.customer.state,
                    city: state.customer.city,
                  }
                : { name: "— select customer —", gstin: null, state: null, city: null }
            }
            docNumber={null}
            issuedOn={state.issuedOn}
            dueOn={state.dueOn}
            placeOfSupplyCode={state.placeOfSupplyCode || companyStateCode}
            lines={state.lines.map((l, i) => ({
              position: i + 1,
              description: l.description,
              hsn: l.hsn,
              qtyMilli: l.qtyMilli,
              unit: l.unit,
              ratePaise: l.ratePaise,
              lineTotalPaise: computedLines[i]?.lineTotalPaise ?? 0n,
              taxRateBps: l.taxRateBps,
            }))}
            totals={totals}
            customerNote={state.customerNote || undefined}
            status="draft"
            sendChannel={state.sendVia}
          />
        </aside>
      </div>

      {/* Sticky footer with the action group */}
      <footer className="sticky bottom-0 z-10 flex shrink-0 flex-wrap items-center gap-2.5 border-t border-line bg-paper px-6 py-3 shadow-lg">
        <p className="min-w-0 flex-1 text-xs text-muted">
          {ready ? (
            <>
              <strong className="text-ink">Ready to issue.</strong> GSTIN + discount checks pass.
            </>
          ) : qtyError ? (
            <span className="text-rose-700" role="alert">
              {qtyError}
            </span>
          ) : (
            "Pick a customer + add at least one line with a description."
          )}
        </p>
        <div className="flex items-center gap-2">
          {/* Delete only shows when the operator is editing an *existing*
           *  draft (arrived via `/sales/invoices/$invoiceId/edit`). The new-invoice
           *  flow autosaves in the background, but the user hasn't
           *  consciously committed yet — surfacing a Delete affordance
           *  there is confusing because there's nothing they remember
           *  putting there. (Issue raised by the operator.) */}
          {loadDraftId && state.draftId ? (
            <button
              type="button"
              onClick={() => setDeleteOpen(true)}
              disabled={issue.isPending || deleteDraft.isPending}
              className="inline-flex h-9 items-center gap-1.5 rounded-sm border border-bad-100 bg-bad-50 px-3 text-xs font-semibold text-bad transition-colors hover:bg-bad-100 hover:border-bad disabled:pointer-events-none disabled:opacity-50"
            >
              <Trash2 size={14} />
              <span>Delete draft</span>
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => navigate({ to: "/sales/invoices" })}
            disabled={issue.isPending}
            className="inline-flex h-9 items-center gap-1.5 rounded-sm px-3 text-sm font-medium text-ink-2 transition-colors hover:bg-bg hover:text-ink disabled:pointer-events-none disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              void handleSaveDraft();
            }}
            disabled={!ready || issue.isPending || updateDraft.isPending || createDraft.isPending}
            className="inline-flex h-9 items-center justify-center gap-1.5 rounded-sm border border-line bg-paper px-3.5 text-xs font-semibold text-ink transition-colors hover:border-line-2 hover:bg-bg disabled:pointer-events-none disabled:opacity-50"
          >
            <FileText size={14} />
            <span>{updateDraft.isPending || createDraft.isPending ? "Saving…" : "Save draft"}</span>
          </button>
          <SplitIssueButton
            sendVia={state.sendVia}
            onIssue={handleIssue}
            disabled={!ready || issue.isPending || !companyStateCode}
            pending={issue.isPending}
          />
        </div>
      </footer>

      <HsnLookupDialog
        open={hsnLookupOpen}
        onOpenChange={setHsnLookupOpen}
        onSelect={(entry) => {
          setState((prev) => {
            // Target the first line whose HSN is empty; if all lines
            // already have an HSN, apply to the last line (the
            // operator's most recent add). This mirrors how Zoho /
            // QuickBooks handle "quick-pick then keep typing".
            const emptyIdx = prev.lines.findIndex((l) => !l.hsn.trim());
            const targetIdx = emptyIdx === -1 ? prev.lines.length - 1 : emptyIdx;
            const taxBps = Math.round(entry.defaultTaxPercent * 100);
            return {
              ...prev,
              lines: prev.lines.map((l, i) =>
                i === targetIdx ? { ...l, hsn: entry.code, taxRateBps: taxBps } : l,
              ),
            };
          });
        }}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete this draft?"
        description={
          state.customer
            ? `${state.customer.name}'s draft will be removed permanently. No document number has been issued, so nothing to roll back.`
            : "This draft will be removed permanently. No document number has been issued, so nothing to roll back."
        }
        intent="danger"
        confirmLabel={deleteDraft.isPending ? "Deleting…" : "Delete draft"}
        onConfirm={() => {
          if (!state.draftId) return;
          deleteDraft.mutate({ invoiceId: state.draftId });
        }}
      />

      <ConfirmDialog
        open={currencySwitchTarget !== null}
        onOpenChange={(open) => {
          if (!open) setCurrencySwitchTarget(null);
        }}
        title={`Switch invoice currency to ${currencySwitchTarget ?? ""}?`}
        description={
          state.currencyCode === "INR"
            ? `Your line rates were entered in INR. Switching to ${currencySwitchTarget} will clear them — re-enter each rate in ${currencySwitchTarget}.`
            : currencySwitchTarget === "INR"
              ? `Your line rates were entered in ${state.currencyCode}. Switching to INR will clear them — re-enter each rate in INR.`
              : `Your line rates were entered in ${state.currencyCode}. Switching to ${currencySwitchTarget} will clear them — re-enter each rate in ${currencySwitchTarget}.`
        }
        intent="default"
        confirmLabel="Switch & clear rates"
        onConfirm={() => {
          if (currencySwitchTarget) {
            applyCurrencyChange(currencySwitchTarget, true);
          }
          setCurrencySwitchTarget(null);
        }}
      />

      {deleteDraft.error ? (
        <Alert intent="bad" title="Couldn't delete draft" description={describeComposerMutationError(deleteDraft.error)} />
      ) : null}

      {issue.error ? (
        <Alert intent="bad" title="Couldn't issue invoice" description={describeComposerMutationError(issue.error)} />
      ) : null}
    </div>
  );
}

/**
 * Same shape as `describeInvoiceMutationError` on the detail page — swaps raw
 * domain / Zod error messages for plain-English copy on the composer's
 * mutation surfaces. Kept local to avoid a cross-import through the barrel.
 */
function describeComposerMutationError(err: {
  data?: { code?: string | undefined } | undefined | null;
  message?: string | undefined | null;
} | null | undefined): string {
  if (!err) return "";
  const msg = err.message ?? "";
  if (/Invoice [0-9a-f-]{36} is (issued|paid|partially_paid|overdue|void|reversed); only drafts/i.test(msg)) {
    const status = msg.match(/is (\w+); only drafts/i)?.[1];
    return status
      ? `This invoice is ${status} — only drafts can be edited or deleted. Refresh to see the current state.`
      : "This invoice is no longer a draft. Refresh to see the current state.";
  }
  if (/At least one invoice line is required/i.test(msg)) {
    return "Add at least one line with a description before issuing.";
  }
  if (/customer.*not.*found|Customer [0-9a-f-]{36} not found/i.test(msg)) {
    return "The selected customer no longer exists. Pick another customer.";
  }
  if (/^\[\s*\{[\s\S]*"code"\s*:\s*"(invalid_|too_small|too_big|custom)/.test(msg)) {
    return "Some of the entered details aren't valid. Check the highlighted rows and try again.";
  }
  switch (err.data?.code) {
    case "PARSE_ERROR":
    case "BAD_REQUEST":
      return "Some of the entered details aren't valid. Check the highlighted rows and try again.";
    case "NOT_FOUND":
      return "The requested invoice no longer exists. Refresh and try again.";
    case "PRECONDITION_FAILED":
      return "This invoice is no longer in a state that supports this action. Refresh and try again.";
    case "CONFLICT":
      return "This action conflicts with another change on the invoice. Refresh and try again.";
    case "UNAUTHORIZED":
    case "FORBIDDEN":
      return "You don't have permission to complete this action.";
    default:
      return "Couldn't complete this action. Try again in a moment.";
  }
}

// ── "Duplicate previous" button ──────────────────────────────────────

type SaveStatusTone = "idle" | "saving" | "saved";

function saveStatusClassName(tone: SaveStatusTone): string {
  const base =
    "inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap";
  if (tone === "saving") return `${base} bg-amber-50 text-amber-700`;
  if (tone === "saved") return `${base} bg-emerald-50 text-emerald-700`;
  return `${base} bg-raised text-muted`;
}

function saveStatusDotClassName(tone: SaveStatusTone): string {
  const base = "h-1.5 w-1.5 rounded-full";
  if (tone === "saving") return `${base} animate-pulse bg-amber-500`;
  if (tone === "saved") return `${base} bg-emerald-500`;
  return `${base} bg-line-2`;
}

/**
 * Loads the most-recently-issued invoice into a fresh draft via
 * `invoices.clone`. Skipped while the operator is still in the
 * middle of an autosave or an issue.
 *
 * On success, navigates to the editor for the new draft. The
 * composer state hydrates from the loaded draft's data — same code
 * path as opening an existing draft from the list.
 */
function DuplicatePreviousButton({
  disabled,
  currentDraftId,
  companyStateCode,
  onCloned,
}: {
  disabled: boolean;
  currentDraftId: string | null;
  companyStateCode: string;
  onCloned: (invoiceId: string) => void;
}) {
  // Pick "most recent issued" from the list query — already cached on
  // the page header. Skip when there's no source to duplicate.
  const list = trpc.invoices.list.useQuery({
    limit: 1,
    status: "issued",
  });
  const sourceId = list.data?.items[0]?.id;

  const clone = trpc.invoices.clone.useMutation({
    onSuccess: (data) => onCloned(data.invoice.id),
  });

  if (currentDraftId) return null; // editing an existing draft — no duplicate
  if (!companyStateCode) return null;

  return (
    <Button
      type="button"
      intent="ghost"
      size="sm"
      disabled={disabled || clone.isPending || !sourceId}
      title={!sourceId ? "No prior invoice to duplicate yet" : undefined}
      className="h-9 border border-line bg-paper px-3 text-xs font-semibold text-ink-2 hover:bg-bg"
      onClick={() => {
        if (!sourceId) return;
        clone.mutate({ invoiceId: sourceId });
      }}
    >
      <CopyIcon size={14} />
      <span>{clone.isPending ? "Duplicating…" : "Duplicate previous"}</span>
    </Button>
  );
}

/** Re-renders every 15s so "Saved Xs ago" stays roughly accurate. */
function useRelativeSavedLabel(at: number | null): string {
  const [, force] = useState(0);
  useEffect(() => {
    if (at === null) return;
    const t = setInterval(() => force((n) => n + 1), 15_000);
    return () => clearInterval(t);
  }, [at]);
  if (at === null) return "";
  const seconds = Math.max(1, Math.round((Date.now() - at) / 1000));
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  return `${hours}h ago`;
}

// ── Split "Issue and send" button ───────────────────────────────────

function SplitIssueButton({
  sendVia,
  onIssue,
  disabled,
  pending,
}: {
  sendVia: ComposerSendChannel;
  onIssue: (channel: ComposerSendChannel) => void;
  disabled?: boolean;
  pending?: boolean;
}) {
  const label = pending ? "Issuing…" : labelFor(sendVia);
  return (
    <div className="inline-flex h-9 shrink-0 overflow-hidden rounded-sm">
      <button
        type="button"
        onClick={() => onIssue(sendVia)}
        disabled={disabled}
        className="inline-flex h-9 items-center gap-1.5 bg-ink px-3.5 text-sm font-semibold text-paper transition-colors hover:bg-ink-2 disabled:pointer-events-none disabled:opacity-50"
      >
        <ArrowRight size={14} strokeWidth={2.2} />
        <span>{label}</span>
      </button>
      <DropdownMenuRoot>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label="Pick send channel"
            className="grid h-9 w-8 place-items-center border-l border-paper/10 bg-ink-2 text-paper transition-colors hover:bg-ink disabled:pointer-events-none disabled:opacity-50"
          >
            <ChevronDown size={12} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => onIssue("whatsapp")}>
            Issue and send via WhatsApp
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onIssue("email")}>
            Issue and send via Email
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onIssue("print-later")}>
            Issue and mark print later
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onIssue("issue-only")}>
            Issue only (no send)
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenuRoot>
    </div>
  );
}

function labelFor(c: ComposerSendChannel): string {
  switch (c) {
    case "whatsapp":
      return "Issue and send via WhatsApp";
    case "email":
      return "Issue and send via Email";
    case "print-later":
      return "Issue and mark print later";
    case "issue-only":
      return "Issue only";
  }
}

// ── helpers ────────────────────────────────────────────────────────

/**
 * Build the create/update-draft payload from the current state.
 * Returns `null` when the form isn't ready (no customer or empty
 * lines) — the caller short-circuits.
 */
function toPersistInput(state: ComposerState): {
  customerId: string;
  issuedOn: string;
  dueOn: string;
  placeOfSupply: string;
  lines: Array<{
    description: string;
    hsn: string;
    unit: string;
    qtyMilli: number;
    ratePaise: string;
    discountBps: number;
    taxRateBps: number;
    rateForeignMinor?: string;
  }>;
  customerNote?: string;
  internalNote?: string;
  poReference?: string;
  declaration?: string;
  currencyCode?: string;
  foreignAmountMinor?: string;
  fxRateToInrPaise?: string;
  fxRateDate?: string;
  taxTreatment?: TaxTreatment;
} | null {
  if (!state.customer) return null;
  const validLines = state.lines.filter((l) => l.description.trim().length > 0);
  if (validLines.length === 0) return null;
  if (!state.placeOfSupplyCode) return null;
  const forceZeroTax = TAX_TREATMENT_ZERO_RATED_SET.has(state.taxTreatment);
  const isForeign = state.currencyCode !== "INR";
  const fxRateToInrPaise = isForeign ? majorToMinor(state.fxRate) : 0n;
  // ADR-0020 — for foreign invoices, derive line `ratePaise` from the
  // operator-typed `rateForeignMinor` * booked FX rate. The wire still
  // sends `ratePaise` for backwards compatibility with the existing
  // schema; the server recomputes via `convertForeignRateToPaise` and
  // overwrites, so a mild SPA/server drift would self-heal.
  let foreignTotalMinor = 0n;
  const wireLines = validLines.map((l) => {
    const rateForeignMinor = l.rateForeignMinor ?? 0n;
    const derivedRatePaise =
      isForeign && fxRateToInrPaise > 0n
        ? convertForeignRateToPaise(rateForeignMinor, fxRateToInrPaise)
        : l.ratePaise;
    if (isForeign) {
      // Track foreign total for the invoice-level snapshot below.
      const lineGross = (rateForeignMinor * BigInt(l.qtyMilli)) / 1000n;
      const lineDisc = (lineGross * BigInt(l.discountBps)) / 10000n;
      const lineTaxable = lineGross - lineDisc;
      const lineTax = (lineTaxable * BigInt(forceZeroTax ? 0 : l.taxRateBps)) / 10000n;
      foreignTotalMinor += lineTaxable + lineTax;
    }
    return {
      ...(l.itemId ? { itemId: l.itemId } : {}),
      description: l.description.trim(),
      hsn: l.hsn || "0000",
      unit: l.unit,
      qtyMilli: l.qtyMilli,
      ratePaise: derivedRatePaise.toString(),
      discountBps: l.discountBps,
      taxRateBps: forceZeroTax ? 0 : l.taxRateBps,
      ...(isForeign ? { rateForeignMinor: rateForeignMinor.toString() } : {}),
    };
  });
  return {
    customerId: state.customer.id,
    issuedOn: state.issuedOn,
    dueOn: state.dueOn,
    placeOfSupply: state.placeOfSupplyCode,
    lines: wireLines,
    ...(state.customerNote.trim() ? { customerNote: state.customerNote.trim() } : {}),
    ...(state.internalNote.trim() ? { internalNote: state.internalNote.trim() } : {}),
    ...(state.poReference.trim() ? { poReference: state.poReference.trim() } : {}),
    ...(state.declaration.trim() ? { declaration: state.declaration.trim() } : {}),
    ...(state.warehouseId.trim() ? { warehouseId: state.warehouseId.trim() } : {}),
    ...(isForeign
      ? {
          currencyCode: state.currencyCode,
          // Derived from line totals — operator no longer types this.
          foreignAmountMinor: foreignTotalMinor.toString(),
          fxRateToInrPaise: fxRateToInrPaise.toString(),
          fxRateDate: state.fxRateDate || state.issuedOn,
        }
      : { currencyCode: "INR" }),
    taxTreatment: state.taxTreatment,
  };
}

/** Stable signature for the autosave debounce. JSON-safe; bigints → strings. */
function serialize(state: ComposerState): string {
  return JSON.stringify({
    customerId: state.customer?.id,
    issuedOn: state.issuedOn,
    dueOn: state.dueOn,
    placeOfSupply: state.placeOfSupplyCode,
    poReference: state.poReference,
    customerNote: state.customerNote,
    internalNote: state.internalNote,
    declaration: state.declaration,
    warehouseId: state.warehouseId,
    currencyCode: state.currencyCode,
    fxRate: state.fxRate,
    fxRateDate: state.fxRateDate,
    taxTreatment: state.taxTreatment,
    lines: state.lines.map((l) => ({
      ...l,
      ratePaise: l.ratePaise.toString(),
      rateForeignMinor: l.rateForeignMinor?.toString() ?? null,
    })),
  });
}

function decimalInput(value: string): string {
  return value
    .replace(/[^\d.]/g, "")
    .replace(/(\..*)\./g, "$1")
    .slice(0, 16);
}

function majorToMinor(value: string): bigint {
  const cleaned = value.replace(/[,\s]/g, "");
  if (!/^\d*(\.\d{0,2})?$/.test(cleaned) || cleaned === "" || cleaned === ".") return 0n;
  const [whole = "0", fraction = ""] = cleaned.split(".");
  return BigInt(whole || "0") * 100n + BigInt(fraction.padEnd(2, "0").slice(0, 2) || "0");
}

function minorToMajor(value: bigint): string {
  const sign = value < 0n ? "-" : "";
  const abs = value < 0n ? -value : value;
  return `${sign}${abs / 100n}.${(abs % 100n).toString().padStart(2, "0")}`;
}

function formatForeign(currencyCode: string, minor: bigint): string {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currencyCode,
  }).format(Number(minor) / 100);
}

/**
 * `'Net 15 days'` / `'Net 15'` / `'15'` → 15.
 * Non-numeric immediate-payment terms (`Advance`, `Prepaid`, `COD`,
 * `On receipt`, `Immediate`) → 0 so the composer sets due = issue and honours
 * the customer's stored term instead of silently falling back to Net-15.
 * Anything else → null.
 */
function parseTermDays(input: string | null): number | null {
  if (!input) return null;
  const trimmed = input.trim();
  if (/^(advance|prepaid|cod|cash on delivery|on receipt|immediate|due on receipt)$/i.test(trimmed)) {
    return 0;
  }
  const m = /(\d+)/.exec(trimmed);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n >= 0 && n <= 365 ? n : null;
}
